import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { app } from '../../firebase';

function TargetAnalysisComp() {
  const db = getFirestore(app);
  const monthlyTarget = 730800;

  const [courseStats, setCourseStats] = useState([]);
  const [expectedIncome, setExpectedIncome] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const coursesSnapshot = await getDocs(collection(db, 'courses'));

      const courseMap = {};

      coursesSnapshot.forEach(doc => {
        const course = doc.data();
        const name = course.mcourseName;
        const price = parseInt(course.feeDeposit || "0");

        if (name) {
          courseMap[name] = {
            name,
            fee: price,
            ongoing: 0,
            completed: 0,
            deferred: 0,
            dropped: 0,
            neededStudents: 0,
            expectedIncome: 0,
          };
        }
      });

      usersSnapshot.forEach(doc => {
        const user = doc.data();
        const courseName = user.course;
        const status = user.userStatus;

        if (courseMap[courseName]) {
          switch (status) {
            case 'Student':
              courseMap[courseName].ongoing += 1;
              break;
            case 'Completed':
              courseMap[courseName].completed += 1;
              break;
            case 'Deferred':
              courseMap[courseName].deferred += 1;
              break;
            case 'Dropped':
              courseMap[courseName].dropped += 1;
              break;
            default:
              break;
          }
        }
      });

      const topCourses = Object.values(courseMap)
        .sort((a, b) => b.ongoing - a.ongoing)
        .slice(0, 5);

      const totalWeight = topCourses.reduce((sum, c) => sum + c.fee, 0);
      let totalIncome = 0;

      topCourses.forEach(course => {
        const targetIncome = monthlyTarget * (course.fee / totalWeight);

        const totalStudents = course.ongoing + course.completed + course.deferred + course.dropped;
        let dropoutRate = 0;
        if (totalStudents > 0) {
          dropoutRate = (course.deferred + course.dropped) / totalStudents;
        }

        const baseNeeded = targetIncome / course.fee;
        const adjustedNeeded = dropoutRate < 1 ? baseNeeded / (1 - dropoutRate) : baseNeeded;

        const neededStudents = Math.ceil(adjustedNeeded);
        const income = neededStudents * course.fee;

        course.neededStudents = neededStudents;
        course.expectedIncome = income;

        totalIncome += income;
      });

      setCourseStats(topCourses);
      setExpectedIncome(totalIncome);
    };

    fetchData();
  }, [db]);

  const percentageAchieved = Math.min((expectedIncome / monthlyTarget) * 100, 100);
  const isTargetMet = expectedIncome >= monthlyTarget;

  return (
    <div className="container mt-4">
      <h4 className="mb-4">Target Performance Analysis</h4>

      <div className="row mb-4">
        <div className="col-md-6 mb-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="card-title">Monthly Target</h6>
              <p className="card-text text-primary fs-3">KES {monthlyTarget.toLocaleString()}</p>
              <h6 className="card-title">Total xpected Students</h6>
              <p className="card-text text-primary fs-3"></p>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className={`card shadow-sm ${isTargetMet ? 'border-success' : 'border-danger'}`}>
            <div className="card-body">
              <h5 className="card-title">Expected Income</h5>
              <p className={`card-text fs-4 ${isTargetMet ? 'text-success' : 'text-danger'}`}>
                KES {expectedIncome.toLocaleString()}
              </p>
              <div className="progress">
                <div
                  className={`progress-bar ${isTargetMet ? 'bg-success' : 'bg-danger'}`}
                  role="progressbar"
                  style={{ width: `${percentageAchieved}%` }}
                >
                  {percentageAchieved.toFixed(1)}%
                </div>
              </div>
              <small className="text-muted">
                {isTargetMet ? '🎯 Target met or exceeded!' : '⚠️ Below target'}
              </small>
            </div>
          </div>
        </div>
      </div>

      {courseStats.length > 0 && (
        <>
          <h5 className="mb-3">Course-wise Income Breakdown</h5>
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                <th>Course</th>
                <th>Fee</th>
                <th>Needed Students</th>
                <th>Expected Income</th>
              </tr>
            </thead>
            <tbody>
              {courseStats.map((course, idx) => (
                <tr key={idx}>
                  <td>{course.name}</td>
                  <td>KES {course.fee.toLocaleString()}</td>
                  <td>{course.neededStudents}</td>
                  <td>KES {course.expectedIncome.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export default TargetAnalysisComp;